import { useEffect } from 'react'
import { FileItem } from '@/types'
import { useTagSelection } from './useTagSelection'
import { useTagRecommendations } from './useTagRecommendations'
import { useContentTagSuggestions } from './useContentTagSuggestions'
import { useTagManagement } from './useTagManagement'

export interface UseSmartTagSuggestionsProps {
  file: FileItem
  existingTags: string[]
  isOpen: boolean
  onTagsAdded: (addedTags: string[]) => void
  onClose: () => void
}

export function useSmartTagSuggestions({
  file,
  existingTags,
  isOpen,
  onTagsAdded,
  onClose,
}: UseSmartTagSuggestionsProps) {
  const tagSelection = useTagSelection()
  const recommendations = useTagRecommendations()
  const contentSuggestions = useContentTagSuggestions()
  const { addMultipleTags, isAdding } = useTagManagement()

  // Fetch initial recommendations when modal opens
  useEffect(() => {
    if (isOpen) {
      recommendations.fetchRecommendations(file.id, existingTags)
      tagSelection.clearSelection()
      recommendations.clearError()
      contentSuggestions.clearError()
    }
  }, [isOpen, file.id, existingTags])

  // Re-fetch recommendations when method changes
  useEffect(() => {
    if (isOpen) {
      recommendations.fetchRecommendations(file.id, existingTags)
    }
  }, [recommendations.method])

  const handleMethodChange = (method: typeof recommendations.method) => {
    recommendations.setMethod(method)
  }

  const handleContentAnalyze = () => {
    contentSuggestions.fetchContentSuggestions(file.id, existingTags)
  }

  const handleAddSelectedTags = async () => {
    if (tagSelection.selectionCount === 0) return

    const success = await addMultipleTags(file.id, tagSelection.selectedTagsArray)

    if (success) {
      onTagsAdded(tagSelection.selectedTagsArray)
      onClose()
    }
  }

  return {
    // Selection state
    ...tagSelection,

    // Recommendations state
    recommendations: recommendations.recommendations,
    recommendationsLoading: recommendations.isLoading,
    recommendationsError: recommendations.error,
    method: recommendations.method,
    handleMethodChange,

    // Content suggestions state
    contentSuggestions: contentSuggestions.suggestions,
    contentLoading: contentSuggestions.isLoading,
    contentError: contentSuggestions.error,
    contentOptions: contentSuggestions.options,
    setContentOptions: contentSuggestions.setOptions,
    handleContentAnalyze,

    // Actions
    handleAddSelectedTags,
    isAdding,

    // Clear errors
    clearRecommendationsError: recommendations.clearError,
    clearContentError: contentSuggestions.clearError,
  }
}
